import { useContext } from 'react';

import type { Discipline } from '@cognite/apm-config';

import { getUseConfiguredDisciplinesContext } from './use-configured-disciplines.context';

export interface DisciplineOption {
  label: string;
  value: Discipline;
}

type UseDisciplinesReturn = {
  configuredDisciplines?: Discipline[];
  configuredDisciplineOptions: DisciplineOption[];
  selectedDiscipline?: DisciplineOption;
  handleSaveDiscipline: (disciplineToSave: Discipline) => void;
};

export const useDisciplines = (): UseDisciplinesReturn => {
  const {
    useCurrentUserContext,
    useAppConfigContext,
    useUpsertCurrentUserPreferences,
  } = useContext(getUseConfiguredDisciplinesContext());
  const { appConfig } = useAppConfigContext();
  const { user } = useCurrentUserContext();
  const { mutateAsync: upsertUserPreferences } =
    useUpsertCurrentUserPreferences();

  const configuredDisciplines = appConfig?.featureConfiguration?.disciplines;
  const userDisciplinePreference = user?.preferences?.infield?.discipline;

  const configuredDisciplineOptions: DisciplineOption[] =
    configuredDisciplines?.map((discipline) => ({
      label: discipline.name,
      value: discipline,
    })) || [];

  const selectedDiscipline = configuredDisciplineOptions.find(
    ({ value }) => value.externalId === userDisciplinePreference?.externalId
  );

  const handleSaveDiscipline = (disciplineToSave: Discipline) => {
    // NOTE: disciplineToSave likely comes from configuredDisciplines already, so it should always be found there, and thus this lookup is defensive.
    // If the two objects have the same externalId but are otherwise different, the object from the configuredDisciplines list will be used
    const selectedDiscipline: Discipline | undefined =
      configuredDisciplines?.find(
        (discipline) => discipline.externalId === disciplineToSave.externalId
      );

    if (!selectedDiscipline) return;

    upsertUserPreferences({
      preference: 'discipline',
      data: selectedDiscipline,
    });
  };

  return {
    configuredDisciplines,
    configuredDisciplineOptions,
    selectedDiscipline,
    handleSaveDiscipline,
  };
};
